import { z } from "zod";
import { BoardCirculationSchema, BoardPackContentManifestSchema, BoardPackManifestEntryKindSchema, } from "./board-meetings.js";
export const PortalPublishedSignerSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(200),
    role: z.enum(["external", "internal"]),
    signedAt: z.string().datetime().nullable(),
});
export const PortalPublishedSignatureSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(500),
    status: z.enum([
        "pending",
        "sent",
        "partially_signed",
        "counter_sign_pending",
        "completed",
        "expired",
        "revoked",
        "cancelled",
    ]),
    signers: z.array(PortalPublishedSignerSchema),
    expiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    signedDocumentAvailable: z.boolean(),
    certificateAvailable: z.boolean(),
});
export const PortalPublishedSignatureListResponseSchema = z.object({
    signatures: z.array(PortalPublishedSignatureSchema),
    total: z.number().int().nonnegative(),
});
export const PortalPublishedMeetingSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(240),
    platform: z.string().min(1).max(40),
    status: z.enum(["uploaded", "processing", "ready", "failed"]),
    recordedAt: z.string().datetime(),
    durationSeconds: z.number().int().nonnegative(),
    transcriptStatus: z.enum(["not_requested", "queued", "processing", "ready", "failed"]),
    summaryStatus: z.enum(["not_requested", "queued", "processing", "ready", "failed"]),
    projectId: z.string().uuid().nullable(),
    downloadAvailable: z.boolean(),
});
export const PortalPublishedMeetingListResponseSchema = z.object({
    meetings: z.array(PortalPublishedMeetingSchema),
    total: z.number().int().nonnegative(),
});
export const PortalPublishedInvoiceSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(300),
    amount: z.number().nonnegative(),
    currency: z.string().min(3).max(3),
    dueDate: z.string().date(),
    recurrence: z.enum(["once", "monthly", "quarterly", "annual"]),
    category: z.string().max(120).nullable(),
    notes: z.string().max(2000).nullable(),
    source: z.enum(["manual", "qbo_recurring", "qbo_ar", "pipeline", "receipt"]),
    status: z.enum(["scheduled", "overdue"]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PortalPublishedInvoiceListResponseSchema = z.object({
    invoices: z.array(PortalPublishedInvoiceSchema),
    total: z.number().int().nonnegative(),
});
export const PortalPublishedApprovalSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    title: z.string().min(1).max(240),
    status: z.enum(["ready", "approved"]),
    requestedOutcome: z.enum(["approve", "advise", "choose_option", "challenge", "note"]).nullable(),
    decisionOwnerEmail: z.string().email().nullable(),
    decisionSought: z.string().max(2000).nullable(),
    recommendation: z.string().max(4000).nullable(),
    risks: z.array(z.string().max(500)),
    approvedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
});
export const PortalPublishedApprovalListResponseSchema = z.object({
    approvals: z.array(PortalPublishedApprovalSchema),
    total: z.number().int().nonnegative(),
});
export const PortalBoardPackReviewChangeSchema = z.object({
    changeType: z.enum(["added", "removed", "changed"]),
    kind: BoardPackManifestEntryKindSchema,
    key: z.string().min(1).max(240),
    title: z.string().min(1).max(500),
    sectionKey: z.string().max(80).nullable(),
    fromStatus: z.string().max(80).nullable(),
    toStatus: z.string().max(80).nullable(),
});
export const PortalBoardPackReviewComparisonSchema = z.object({
    previousVersion: z.number().int().positive().nullable(),
    previousGeneratedAt: z.string().datetime().nullable(),
    summary: z.object({
        added: z.number().int().nonnegative(),
        removed: z.number().int().nonnegative(),
        changed: z.number().int().nonnegative(),
        unchanged: z.number().int().nonnegative(),
    }),
    changes: z.array(PortalBoardPackReviewChangeSchema),
    warnings: z.array(z.string()).default([]),
});
export const PortalPublishedBoardPackSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    title: z.string().min(1).max(240),
    meetingDate: z.string().datetime(),
    meetingStatus: z.enum(["draft", "published", "in_progress", "completed", "archived"]),
    version: z.number().int().positive(),
    status: z.enum(["draft", "final", "superseded"]),
    pageCount: z.number().int().positive().nullable(),
    generatedAt: z.string().datetime().nullable(),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    downloadAvailable: z.boolean(),
    contentManifest: BoardPackContentManifestSchema.nullable().default(null),
    reviewComparison: PortalBoardPackReviewComparisonSchema.nullable().default(null),
    directorEvidence: BoardCirculationSchema.nullable().default(null),
});
export const PortalPublishedBoardPackListResponseSchema = z.object({
    boardPacks: z.array(PortalPublishedBoardPackSchema),
    total: z.number().int().nonnegative(),
});
export const PortalPublishedSupplierItemSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(320),
    filename: z.string().min(1).max(500),
    supplierName: z.string().max(240).nullable(),
    receiptDate: z.string().date().nullable(),
    totalAmount: z.number().nonnegative().nullable(),
    taxAmount: z.number().nonnegative().nullable(),
    currency: z.string().min(3).max(3),
    category: z.string().max(100).nullable(),
    notes: z.string().max(1000).nullable(),
    status: z.enum(["pending_review", "approved", "rejected"]),
    extractionStatus: z.enum(["pending", "processing", "complete", "failed"]),
    reviewedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    downloadAvailable: z.boolean(),
});
export const PortalPublishedSupplierItemListResponseSchema = z.object({
    supplierItems: z.array(PortalPublishedSupplierItemSchema),
    total: z.number().int().nonnegative(),
});
export const PortalPublishedDownloadResponseSchema = z.object({
    downloadUrl: z.string().url(),
    filename: z.string().min(1),
});
//# sourceMappingURL=portal-published-objects.js.map